import { Heightfield } from '../terrain/heightfield'
import { ObstacleField } from '../physics/obstacles'
import { Skier, emptyInput } from '../physics/skier'
import type { InputFrame, Pose } from '../physics/skier'
import { Sluff } from '../physics/sluff'
import { clamp } from '../core/math'

/**
 * Ghosts, as recorded input streams rather than recorded positions.
 *
 * The simulation is deterministic and verified as such, so replaying the inputs
 * reproduces the run exactly — which means a ghost costs two bytes per tick
 * instead of a position sample, and the same recording is precisely what
 * Phase 4's server needs to re-simulate a submitted run and check the time
 * (spec §10: "clients submit the input stream, not the score").
 *
 * Steer is quantised *before* the live run consumes it, not on the way into the
 * recording. Quantising afterwards would mean the ghost replayed slightly
 * different inputs than the run that produced it, and on a 90-second descent
 * that divergence compounds into a visibly different line.
 */

/** Steering resolution. 1/127 is far finer than a thumb can hold. */
export const STEER_STEPS = 127

export function quantiseSteer(steer: number): number {
  return Math.round(clamp(steer, -1, 1) * STEER_STEPS) / STEER_STEPS
}

export interface GhostRecording {
  readonly slopeId: string
  readonly slopeVersion: number
  readonly startX: number
  readonly startZ: number
  /** 2 bytes per tick: steer, then a flag byte. */
  readonly frames: Uint8Array
  readonly tickCount: number
  readonly totalTime: number
  readonly splits: ReadonlyArray<number | null>
  readonly recordedAt: number
}

export class GhostRecorder {
  private bytes: Uint8Array
  private ticks = 0

  constructor(capacityTicks = 60 * 60 * 4) {
    this.bytes = new Uint8Array(capacityTicks * 2)
  }

  reset(): void {
    this.ticks = 0
  }

  record(input: InputFrame): void {
    const o = this.ticks * 2
    if (o + 1 >= this.bytes.length) {
      const grown = new Uint8Array(this.bytes.length * 2)
      grown.set(this.bytes)
      this.bytes = grown
    }
    this.bytes[o] = Math.round((clamp(input.steer, -1, 1) + 1) * 0.5 * 254)
    this.bytes[o + 1] =
      (input.tuck ? 1 : 0) | (input.brake ? 2 : 0) | (input.pop ? 4 : 0)
    this.ticks++
  }

  finish(
    slopeId: string, slopeVersion: number,
    start: { x: number; z: number },
    totalTime: number, splits: ReadonlyArray<number | null>,
    stamp: number,
  ): GhostRecording {
    return {
      slopeId, slopeVersion,
      startX: start.x, startZ: start.z,
      frames: this.bytes.slice(0, this.ticks * 2),
      tickCount: this.ticks,
      totalTime,
      splits: [...splits],
      recordedAt: stamp,
    }
  }
}

export function decodeFrame(rec: GhostRecording, tick: number, out: InputFrame): InputFrame {
  const o = tick * 2
  if (tick < 0 || tick >= rec.tickCount) {
    out.steer = 0; out.tuck = false; out.brake = false; out.pop = false
    return out
  }
  out.steer = quantiseSteer((rec.frames[o] / 254) * 2 - 1)
  const flags = rec.frames[o + 1]
  out.tuck = (flags & 1) !== 0
  out.brake = (flags & 2) !== 0
  out.pop = (flags & 4) !== 0
  return out
}

/**
 * Replays a recording alongside the live run, one tick for one tick, so the
 * ghost is always exactly where it was at this point in *its* run.
 */
export class GhostPlayer {
  readonly skier: Skier
  private readonly sluff: Sluff
  private readonly input: InputFrame = emptyInput()
  private tick = 0
  finished = false

  constructor(
    hf: Heightfield,
    obstacles: ObstacleField,
    readonly recording: GhostRecording,
  ) {
    this.skier = new Skier(hf, obstacles)
    this.sluff = new Sluff(hf)
    this.reset()
  }

  reset(): void {
    this.skier.reset(this.recording.startX, this.recording.startZ, 0)
    this.sluff.reset()
    this.tick = 0
    this.finished = false
  }

  step(dt: number): void {
    if (this.finished) return
    if (this.tick >= this.recording.tickCount) { this.finished = true; return }
    decodeFrame(this.recording, this.tick, this.input)
    this.skier.savePrevPose()
    this.skier.sluffLoad = this.sluff.step(this.skier, dt)
    this.skier.step(this.input, dt)
    this.tick++
  }

  get pose(): Pose {
    return this.skier.curr
  }

  get prevPose(): Pose {
    return this.skier.prev
  }
}

// ---------------------------------------------------------------------------
// Local storage
// ---------------------------------------------------------------------------

const KEY = 'fall-line/best'

interface StoredGhost extends Omit<GhostRecording, 'frames'> {
  framesB64: string
}

/** Personal best per slope. The server-backed leaderboard is Phase 4. */
export function loadBest(slopeId: string, version: number): GhostRecording | null {
  try {
    const raw = localStorage.getItem(`${KEY}/${slopeId}`)
    if (!raw) return null
    const s = JSON.parse(raw) as StoredGhost
    if (s.slopeVersion !== version) return null // design changed; times are void
    const bin = atob(s.framesB64)
    const frames = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) frames[i] = bin.charCodeAt(i)
    return { ...s, frames }
  } catch {
    return null
  }
}

export function saveBest(rec: GhostRecording): void {
  try {
    let bin = ''
    for (let i = 0; i < rec.frames.length; i++) bin += String.fromCharCode(rec.frames[i])
    const { frames: _drop, ...rest } = rec
    const stored: StoredGhost = { ...rest, framesB64: btoa(bin) }
    localStorage.setItem(`${KEY}/${rec.slopeId}`, JSON.stringify(stored))
  } catch {
    // Storage full or blocked — a lost personal best is not worth an exception.
  }
}
